import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import LoadingScreen from '@/pages/LoadingScreen'
import { ReactNode, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { validateUserToken } from '@/api/auth/user'
import { getItem, setItem } from '@/utils/storage'
import { isAxiosError } from '@/api/business'
import { useBaseStore } from '@/store'
import { Store } from '@/store/type'
import NotAllowed from '@/pages/NotAllowed'
import { ROUTES_ACCESS } from '@/components/Sidenav/consts'

interface RequireAuthProps {
    isAllowed: boolean
    redirectPath?: string
    children?: ReactNode
}

const stateSelector = (state: Store) => ({
    user: state.authSlice.state.user,
    profile: state.authSlice.state.profile,
    login: state.authSlice.actions.login,
    logout: state.authSlice.actions.logout,
})

const RequireAuth = ({
    isAllowed,
    redirectPath,
    children,
}: RequireAuthProps) => {
    const { user, profile, login, logout } = useBaseStore(stateSelector)

    const token = getItem(localStorage, 'token')
    const location = useLocation()
    const [canAccessRoute, setCanAccessRoute] = useState(true); 
    const navigate = useNavigate()

    const queryClient = useQueryClient()

    const { mutate, isPending } = useMutation({
        mutationFn: validateUserToken,
        onSuccess: (data) => {
            setItem(localStorage, 'token', token)
            if (data.token) {
                login({
                    token,
                    user: data.token.usuario.nome,
                    profile: data.token.usuario.perfil,
                    userId: data.token.usuario.cod_usuario,
                })
            }
        },
        onError: (err) => {
            if (isAxiosError(err)) {
                if (
                    err.response?.status === 401 ||
                    err.response?.status === 402
                ) {
                    logout(queryClient)
                }
            }
        },
    })
 
    useEffect(() => {  
        if(profile) {
            const canAccess = ROUTES_ACCESS.filter(item => item.accessProfile?.includes(profile || '') && item.route == location.pathname)
              
             if(canAccess.length >= 1) {
                 setCanAccessRoute(true)
             } else {
                 setCanAccessRoute(false)
                 navigate(location.state?.path || '/')
             }

        }
    },[location.pathname, canAccessRoute, profile])

    useEffect(() => {
        if (token && !user) {
            mutate()
        }
    }, [token, mutate])

    if (isPending) return <LoadingScreen />

    if (!token) {
        return (
            <Navigate
                to="/login"
                replace
                state={{ path: redirectPath || location.pathname }}
            />
        )
    }

    if (!isAllowed) {
        return <NotAllowed />
    }

    return children || <Outlet />
}

export default RequireAuth
